import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../../lib/cn.js';

export type ButtonVariant = 'ghost' | 'primary' | 'secondary' | 'link' | 'destructive';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Optional variant sentinel. When provided, renders a `data-variant` attribute
   * that the `.btn-press` CSS utility reads — the `ghost` value opts the button
   * out of the brightness(0.95) press tint per epics.md line 3268. When omitted,
   * no attribute is rendered and behavior is unchanged.
   */
  variant?: ButtonVariant;
}

/**
 * Minimal Button primitive — plain `button` wrapper with the base shadcn-style
 * chrome (rounded, focus ring). No variants / asChild — composition is up to
 * the consumer. Pair with a ButtonColorful-style wrapper for branded CTAs.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, type = 'button', variant, ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      data-variant={variant}
      className={cn(
        'btn-press',
        'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400',
        'disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
